/**
 * The picture a scan sends, made as the iOS app makes it (`AdminScanModeView`, `AdminCardCamera`, `ImageHelper`):
 * the frame guide's crop of the live camera's frame, or a whole imported photo, as a JPEG of at most 500 KB.
 */

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Rect extends Size {
  readonly x: number;
  readonly y: number;
}

/** The most a picture weighs, as iOS sends it. */
export const SCAN_IMAGE_MAX_BYTES = 500 * 1024;
/** An imported photo's long side before the ladder: a phone browser's canvas cannot hold a 48 MP photo. */
export const IMPORT_LONGEST_SIDE = 1600;

const clamp = (n: number, max: number) => Math.min(Math.max(n, 0), max);

/**
 * `rect`, drawn over a video shown with `object-fit: cover` in `view`, in the video's own pixels: what iOS asks
 * of its aspect-fill preview layer (`metadataOutputRectConverted`). Null before the video has a size.
 */
export function coverCrop(view: Size, video: Size, rect: Rect, mirrored = false): Rect | null {
  if (video.width <= 0 || video.height <= 0 || view.width <= 0 || view.height <= 0) return null;
  const scale = Math.max(view.width / video.width, view.height / video.height);
  const left = (view.width - video.width * scale) / 2;
  const top = (view.height - video.height * scale) / 2;
  const x0 = Math.round(clamp((rect.x - left) / scale, video.width));
  const x1 = Math.round(clamp((rect.x + rect.width - left) / scale, video.width));
  const y0 = Math.round(clamp((rect.y - top) / scale, video.height));
  const y1 = Math.round(clamp((rect.y + rect.height - top) / scale, video.height));
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: mirrored ? video.width - x1 : x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/** `size` with its long side at most `longest`, never enlarged. */
export function fitWithin(size: Size, longest: number): Size {
  const scale = Math.min(1, longest / Math.max(size.width, size.height));
  return { width: Math.max(1, Math.round(size.width * scale)), height: Math.max(1, Math.round(size.height * scale)) };
}

/** One try: the share of the picture's size it is drawn at, and the JPEG quality. */
export interface Encoding {
  readonly scale: number;
  readonly quality: number;
}

const QUALITIES = [0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
const SCALES = [1, 0.7, 0.49, 0.343];

/** iOS's tries in order: quality 0.8 down to 0.3, at the full size and then ×0.7 of it per round, and last the smallest once more at 0.3. */
export const ENCODING_LADDER: readonly Encoding[] = [
  ...SCALES.flatMap((scale) => QUALITIES.map((quality) => ({ scale, quality }))),
  { scale: 0.2401, quality: 0.3 },
];

/**
 * The first try that weighs at most `maxBytes`, else the last one made, as iOS keeps its last resort whatever it
 * weighs. `encode` makes one try, or answers null when the browser cannot; null when no try could be made.
 */
export async function compressPicture<T extends { readonly size: number }>(
  encode: (encoding: Encoding) => Promise<T | null>,
  maxBytes = SCAN_IMAGE_MAX_BYTES,
): Promise<T | null> {
  let last: T | null = null;
  for (const encoding of ENCODING_LADDER) {
    const encoded = await encode(encoding);
    if (encoded === null) continue;
    if (encoded.size <= maxBytes) return encoded;
    last = encoded;
  }
  return last;
}

function canvasOf(size: Size, draw: (g: CanvasRenderingContext2D) => void): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const g = canvas.getContext("2d");
  if (!g) return null;
  g.imageSmoothingQuality = "high";
  draw(g);
  return canvas;
}

function jpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/** Draws the picture once at `size`, so a live frame is the one the shutter caught, then walks the ladder over that copy. */
async function encodePicture(size: Size, draw: (g: CanvasRenderingContext2D) => void): Promise<Blob | null> {
  const base = canvasOf(size, draw);
  if (!base) return null;
  let drawn = { scale: 1, canvas: base };
  return compressPicture(async ({ scale, quality }) => {
    if (drawn.scale !== scale) {
      const scaled = {
        width: Math.max(1, Math.round(size.width * scale)),
        height: Math.max(1, Math.round(size.height * scale)),
      };
      const canvas = canvasOf(scaled, (g) => g.drawImage(base, 0, 0, scaled.width, scaled.height));
      if (!canvas) return null;
      drawn = { scale, canvas };
    }
    return jpeg(drawn.canvas, quality);
  });
}

/** The guide's crop of the video's current frame, as a scan sends it. */
export function shotFromVideo(video: HTMLVideoElement, crop: Rect): Promise<Blob | null> {
  return encodePicture(crop, (g) =>
    g.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height),
  );
}

function decode(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("undecodable"));
    image.src = url;
  });
}

/**
 * A picked photo, whole, as a scan sends it. Decoded through an `<img>`, which turns it upright by its EXIF
 * orientation. Null when the browser cannot decode it (HEIC outside Safari): the tray could not show it either.
 */
export async function pictureFromFile(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const image = await decode(url);
    const size = fitWithin({ width: image.naturalWidth, height: image.naturalHeight }, IMPORT_LONGEST_SIDE);
    return await encodePicture(size, (g) => g.drawImage(image, 0, 0, size.width, size.height));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A picture as the data URL the scan route takes. */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return `data:${blob.type || "application/octet-stream"};base64,${btoa(binary)}`;
}
